/**
 * Edge Integrity pipeline.
 *
 * Eliminates the stray-pixel fringe on the 3D body without blurring the design.
 * Designers currently blur their artwork's edges, which suppresses the fringe by
 * destroying contrast at the boundary. The correct fix is the opposite: keep the
 * design pixel-exact and extend its colours *outward*, so whatever bilinear
 * filtering and mipmapping sample outside the region is already correct.
 *
 * Transparency is a first-class technique here, not an edge case — a transparent
 * sleeve region is how you get short sleeves. So two distinct operations are
 * needed, and conflating them corrupts transparent designs:
 *
 *   colour bleed   RGB flows under transparent pixels, alpha untouched.
 *                  Prevents the dark halo around every cut-out.
 *   dilation       region content expands into the gutter and dead space,
 *                  carrying RGB *and* alpha, so a transparent region edge
 *                  produces a transparent gutter rather than an opaque halo.
 *
 * Ported from tools/make_diagnostic_garment.py and cross-checked against it.
 */
import {
  type AlphaHardening,
  CANVAS,
  type EdgeSettings,
  REGION_IDS,
  type RegionId,
} from "./format";
import { GUTTER_PX, REGIONS } from "./regions";
import type { SeamMap } from "./seam-map";

export interface Bitmap {
  width: number;
  height: number;
  /** RGBA, 4 bytes per pixel, row-major. */
  data: Uint8ClampedArray;
}

export function createBitmap(width: number = CANVAS.width, height: number = CANVAS.height): Bitmap {
  return { width, height, data: new Uint8ClampedArray(width * height * 4) };
}

export function cloneBitmap(b: Bitmap): Bitmap {
  return { width: b.width, height: b.height, data: new Uint8ClampedArray(b.data) };
}

function regionMask(b: Bitmap, allowed: readonly RegionId[], gutter: number): Uint8Array {
  const mask = new Uint8Array(b.width * b.height);
  for (const id of allowed) {
    const r = REGIONS[id];
    const x0 = Math.max(0, r.x - gutter);
    const y0 = Math.max(0, r.y - gutter);
    const x1 = Math.min(b.width, r.x + r.w + gutter);
    const y1 = Math.min(b.height, r.y + r.h + gutter);
    for (let y = y0; y < y1; y++) {
      const row = y * b.width;
      for (let x = x0; x < x1; x++) mask[row + x] = 1;
    }
  }
  return mask;
}

/** Stage 0 — clear anything outside the layer's permitted regions. */
export function clipToRegions(
  b: Bitmap,
  allowed: readonly RegionId[] = REGION_IDS,
  gutter = GUTTER_PX,
  { keepColour = false } = {},
): Bitmap {
  const mask = regionMask(b, allowed, gutter);
  for (let i = 0; i < mask.length; i++) {
    if (!mask[i]) {
      const o = i * 4;
      /*
        Alpha always; RGB only when asked.

        Zeroing both makes a texel transparent AND black, and those are
        different claims. Invisible is what is wanted; black is what leaks back
        when a sampler averages across it — the halo `bleedColour` exists to
        prevent, reintroduced by the stage that runs after it.
      */
      if (!keepColour) {
        b.data[o] = 0;
        b.data[o + 1] = 0;
        b.data[o + 2] = 0;
      }
      b.data[o + 3] = 0;
    }
  }
  return b;
}

/** Partial-alpha pixels as a fraction of the region's area. */
export function regionTransparencyProfile(b: Bitmap, id: RegionId) {
  const r = REGIONS[id];
  let partial = 0;
  let clear = 0;
  let opaque = 0;
  for (let y = r.y; y < r.y + r.h; y++) {
    for (let x = r.x; x < r.x + r.w; x++) {
      const a = b.data[(y * b.width + x) * 4 + 3] as number;
      if (a <= 8) clear++;
      else if (a >= 247) opaque++;
      else partial++;
    }
  }
  const area = r.w * r.h;
  return { partial, clear, opaque, area, partialFraction: partial / area };
}

/**
 * Stage 1 — snap alpha to 0/255 near region boundaries.
 *
 * In `auto`, a region carrying meaningful partial alpha is left alone: hardening
 * a soft-edged or sheer design would destroy exactly what the designer drew.
 * Regions that are already essentially binary get hardened, which costs nothing
 * and removes the skin-bleed halo.
 */
export function hardenAlpha(
  b: Bitmap,
  mode: AlphaHardening = "auto",
  edgePx = 2,
  threshold = 128,
): Bitmap {
  if (mode === "never") return b;

  for (const id of REGION_IDS) {
    if (mode === "auto") {
      // 0.5% of a 128x128 region is ~82 pixels — well above antialiasing noise
      // on a hard-edged design, well below any real gradient.
      if (regionTransparencyProfile(b, id).partialFraction > 0.005) continue;
    }

    const r = REGIONS[id];
    for (let y = r.y; y < r.y + r.h; y++) {
      for (let x = r.x; x < r.x + r.w; x++) {
        const nearEdge =
          x < r.x + edgePx ||
          x >= r.x + r.w - edgePx ||
          y < r.y + edgePx ||
          y >= r.y + r.h - edgePx;
        if (!nearEdge) continue;
        const o = (y * b.width + x) * 4 + 3;
        const a = b.data[o] as number;
        if (a > 0 && a < 255) b.data[o] = a >= threshold ? 255 : 0;
      }
    }
  }
  return b;
}

/**
 * Colour bleed — push RGB underneath transparent pixels, leaving alpha alone.
 *
 * Filtering between an opaque pixel and a transparent one interpolates RGB as
 * well as alpha. If the transparent pixel's RGB is black (the default for an
 * untouched buffer), every cut-out gets a dark rim. Bleeding the neighbouring
 * colour under the transparency removes it without changing what is visible.
 */
export function bleedColour(b: Bitmap, passes = 4): Bitmap {
  const { width: w, height: h, data } = b;

  for (let p = 0; p < passes; p++) {
    const hasColour = new Uint8Array(w * h);
    for (let i = 0; i < w * h; i++) {
      const o = i * 4;
      hasColour[i] =
        (data[o + 3] as number) > 0 ||
        (data[o] as number) + (data[o + 1] as number) + (data[o + 2] as number) > 0
          ? 1
          : 0;
    }

    const writes: Array<[number, number, number, number]> = [];
    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const idx = y * w + x;
        if (hasColour[idx]) continue;

        let rs = 0;
        let gs = 0;
        let bs = 0;
        let n = 0;
        for (let dy = -1; dy <= 1; dy++) {
          const ny = y + dy;
          if (ny < 0 || ny >= h) continue;
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const nx = x + dx;
            if (nx < 0 || nx >= w || !hasColour[ny * w + nx]) continue;
            const o = (ny * w + nx) * 4;
            rs += data[o] as number;
            gs += data[o + 1] as number;
            bs += data[o + 2] as number;
            n++;
          }
        }
        if (n > 0) writes.push([idx, rs / n, gs / n, bs / n]);
      }
    }

    // Alpha is deliberately not touched — this only paints under transparency.
    for (const [idx, r, g, bl] of writes) {
      const o = idx * 4;
      data[o] = r;
      data[o + 1] = g;
      data[o + 2] = bl;
    }
  }
  return b;
}

/**
 * Stages 3 + 4 — expand region content into the gutter and dead space.
 *
 * Only pixels *outside* the regions are filled, and they inherit RGB **and
 * alpha**. That is what makes transparent designs safe: a transparent region
 * edge yields a transparent gutter instead of an opaque halo. Averaging is what
 * reconciles cube corners, where three faces contribute.
 */
export function dilate(b: Bitmap, passes = GUTTER_PX + 1): Bitmap {
  const { width: w, height: h, data } = b;

  // Two masks, and they are deliberately not the same set.
  //
  //   protectedPx  may never be written — region interiors, so the design stays
  //                pixel-exact.
  //   source       may be read from — pixels that actually carry alpha.
  //
  // Conflating them sealed only half of every gutter. Every face in this
  // template borders another face across exactly 2px, so the gutter pixel two
  // columns out from an opaque face is also one column out from the *neighbour*
  // — and if that neighbour is empty, it claimed the pixel on pass 1, averaged
  // in nothing but transparency, and locked it at alpha 0 for the remaining
  // passes. Measured on a single filled face: 520 px sealed where 1,040 is the
  // whole gutter. It never showed up in tests because the fixture fills all 18
  // faces, and a real design never does.
  //
  // Reading only from pixels with alpha also keeps the transparency semantics
  // intact rather than by accident: a genuinely transparent region edge — a
  // short sleeve — contributes nothing, so its gutter stays transparent instead
  // of gaining an opaque halo.
  const protectedPx = regionMask(b, REGION_IDS, 0);
  const source = new Uint8Array(w * h);
  for (let i = 0; i < source.length; i++) source[i] = (data[i * 4 + 3] as number) > 0 ? 1 : 0;

  for (let p = 0; p < passes; p++) {
    const snapshot = new Uint8Array(source);
    const writes: Array<[number, number, number, number, number]> = [];

    for (let y = 0; y < h; y++) {
      for (let x = 0; x < w; x++) {
        const idx = y * w + x;
        if (protectedPx[idx] || snapshot[idx]) continue;

        let rs = 0;
        let gs = 0;
        let bs = 0;
        let as = 0;
        let n = 0;
        for (let dy = -1; dy <= 1; dy++) {
          const ny = y + dy;
          if (ny < 0 || ny >= h) continue;
          for (let dx = -1; dx <= 1; dx++) {
            if (dx === 0 && dy === 0) continue;
            const nx = x + dx;
            if (nx < 0 || nx >= w || !snapshot[ny * w + nx]) continue;
            const o = (ny * w + nx) * 4;
            rs += data[o] as number;
            gs += data[o + 1] as number;
            bs += data[o + 2] as number;
            as += data[o + 3] as number;
            n++;
          }
        }
        if (n > 0) writes.push([idx, rs / n, gs / n, bs / n, as / n]);
      }
    }

    for (const [idx, r, g, bl, a] of writes) {
      const o = idx * 4;
      data[o] = r;
      data[o + 1] = g;
      data[o + 2] = bl;
      data[o + 3] = a;
      source[idx] = 1;
    }
  }
  return b;
}

/**
 * Stage 2 — carry colour across a 3D edge.
 *
 * The other stages reason in template space: `dilate` pushes content outward
 * into the gutter, `bleedColour` fills RGB under transparency. Neither can help
 * at a face boundary, because the surface continues onto a face that is
 * somewhere else entirely in the template and rotated on arrival. Only the
 * geometry knows where, which is what `SeamMap` is for.
 *
 * **It only ever fills what is empty.** A texel that already carries alpha is
 * left exactly as drawn — so this can add a fringe where the artwork stopped,
 * and can never alter the artwork itself. That asymmetry is the whole safety
 * argument: the worst case is a band of colour someone did not ask for at the
 * very edge of a face, and the best case is a corner that stops looking cut.
 *
 * Transparency stays first-class, for the same reason `dilate` reads only from
 * pixels with alpha. A deliberately bare face contributes nothing to its
 * neighbours, so a short sleeve does not grow an opaque rim from the torso.
 *
 * Returns the number of texels filled, which is what makes the stage
 * measurable rather than merely enabled.
 */
/**
 * How far the export's seam fill reaches, in texels.
 *
 * The gutter's width, and deliberately far less than the map now carries. One
 * map serves two consumers with opposite appetites: painting wants a band wide
 * enough for a brush to cross a fold, while this rewrites somebody's finished
 * file and should reach no further than an edge artifact does. Passing it
 * explicitly is what keeps the two apart — defaulting to the map's own band
 * would silently widen the export the day the painting band grew.
 */
export const SEAM_BLEED_BAND = 2;

export function seamBleed(b: Bitmap, seam: SeamMap, band = SEAM_BLEED_BAND): number {
  const { width, height, data } = b;
  if (seam.width !== width || seam.height !== height) return 0;

  /*
    Read every source before writing any of them.

    Two faces meeting at an edge are BOTH in the ring, so a texel filled by this
    pass could otherwise become the source for its own neighbour and drag colour
    along the seam — the same failure `mirrorStroke` snapshots against, and it
    would look like a smear that only appears at corners.
  */
  const before = new Uint8ClampedArray(data);
  const depth = Math.max(1, Math.min(band, seam.band));
  let filled = 0;

  for (let i = 0; i < seam.across.length; i++) {
    const to = seam.across[i] ?? -1;
    if (to < 0) continue;
    // Already drawn. Nothing here may touch it.
    if ((before[i * 4 + 3] as number) !== 0) continue;
    // Nothing to carry: the far side is bare too.
    if ((before[to * 4 + 3] as number) === 0) continue;

    /*
      Depth is honoured against the map's own band rather than assumed, so a
      caller can ask for a narrower fringe than the map was built with. Wider is
      refused: the map simply has no answer past its band, and quietly doing
      less than asked beats inventing a partner.
    */
    if (depth < seam.band) {
      const x = i % width;
      const y = (i - x) / width;
      if (!withinDepth(x, y, depth)) continue;
    }

    data[i * 4] = before[to * 4] as number;
    data[i * 4 + 1] = before[to * 4 + 1] as number;
    data[i * 4 + 2] = before[to * 4 + 2] as number;
    data[i * 4 + 3] = before[to * 4 + 3] as number;
    filled++;
  }
  return filled;
}

/**
 * How far every texel is from the nearest rim of its own face, or -1 outside
 * every face. 0 means it IS the rim.
 *
 * Built once and cached, because the obvious per-texel version is the wrong
 * shape here: `seamBlend` asks twice per seam link, and there are 88,064 links
 * on a shirt, so eighteen rect tests an answer is 3.2 million of them. Measured
 * at 30.7ms — nearly two frames at 60fps, on a stage the seam PREVIEW runs
 * every time the composite changes, which during a stroke is every frame.
 *
 * One pass over the region rects fills it in a couple of milliseconds and every
 * later question is an array read. Keyed by size so a differently-sized bitmap
 * cannot silently read another's answers; in practice there is one template.
 */
let depthCache: { width: number; height: number; depth: Int16Array } | null = null;

function rimDepths(width: number, height: number): Int16Array {
  const hit = depthCache;
  if (hit && hit.width === width && hit.height === height) return hit.depth;
  const depth = new Int16Array(width * height).fill(-1);
  for (const id of REGION_IDS) {
    const r = REGIONS[id];
    for (let y = r.y; y < r.y + r.h; y++) {
      if (y < 0 || y >= height) continue;
      const dyTop = y - r.y;
      const dyBottom = r.y + r.h - 1 - y;
      const row = y * width;
      for (let x = r.x; x < r.x + r.w; x++) {
        if (x < 0 || x >= width) continue;
        // First region wins, matching the per-texel version this replaces:
        // regions do not overlap, so the guard only matters at the seams of
        // a hand-edited rect table.
        if (depth[row + x] !== -1) continue;
        depth[row + x] = Math.min(dyTop, dyBottom, x - r.x, r.x + r.w - 1 - x);
      }
    }
  }
  depthCache = { width, height, depth };
  return depth;
}

/**
 * How far the seam blend reaches inward from each rim, in texels.
 *
 * 6 is chosen against how the artifact behaves rather than how wide a fringe
 * looks: a mip level averages 2x2, so the level-N footprint is 2^N texels, and
 * a blend that fades out by 6 keeps the two sides agreeing through roughly the
 * first two or three levels — which is where a seam on a garment viewed at
 * arm's length actually shows.
 */
export const SEAM_BLEND_BAND = 6;

/**
 * Stage 2b — make the two sides of a fold AGREE, rather than filling holes.
 *
 * `seamBleed` above only ever writes a texel whose alpha is zero, which is the
 * right rule for a hole and the wrong one for a seam. Measured on a finished
 * r6-classic shirt: **all 88,064 seam links are skipped because the target is
 * already painted**, so on any garment whose artwork reaches the edge — which
 * is every finished one — that stage does nothing at all.
 *
 * And there is no hole to fill. Running the export over two faces painted flat
 * red and flat blue leaves red to x=229, blue from x=230, alpha 255 across the
 * whole gutter: the padding is already correct. What remains is the COLOUR
 * step, and on the body those two columns are adjacent.
 *
 * The step cannot be padded away. Roblox's template fixes the gutter at 2px, so
 * there is nowhere to put more separation, and a mip level averages across it
 * regardless. The only lever left inside that constraint is to make the two
 * sides meet in the middle.
 *
 * **Both directions are in the map**, so a rim texel on A moves halfway toward
 * B while B's moves halfway toward A — they converge on the same value and the
 * edge stops existing. Weighted by depth so the correction fades inward instead
 * of leaving a second, softer line where the band ends.
 *
 * **This modifies artwork**, which every other stage here refuses to do. That
 * is why it is a separate setting rather than a wider `seamBleed`: the existing
 * stage's safety argument — worst case a fringe nobody asked for — does not
 * cover this, and somebody painting a deliberate hard boundary at a fold should
 * be able to keep it.
 *
 * Returns the number of texels moved.
 */
export function seamBlend(b: Bitmap, seam: SeamMap, band = SEAM_BLEND_BAND): number {
  const { width, height, data } = b;
  if (seam.width !== width || seam.height !== height) return 0;

  // Read every source before writing any: both sides of an edge are in the
  // ring, so writing in place would let a blended texel become the source for
  // its partner and drag the correction along the seam.
  const before = new Uint8ClampedArray(data);
  const reach = Math.max(1, Math.min(band, seam.band));
  const depths = rimDepths(width, height);
  let moved = 0;

  for (let i = 0; i < seam.across.length; i++) {
    const to = seam.across[i] ?? -1;
    if (to < 0) continue;
    // Nothing to reconcile unless both sides carry paint. A bare face still
    // contributes nothing to its neighbour, exactly as in `seamBleed`.
    if ((before[i * 4 + 3] as number) === 0) continue;
    if ((before[to * 4 + 3] as number) === 0) continue;

    const d = depths[i] as number;
    if (d < 0 || d >= reach) continue;

    /*
      Move BOTH ends of the link, rather than trusting the reverse to exist.

      It usually does not. Measured on r6-classic: `torso.right` sends 1,792
      links into `torso.front` and `torso.front` sends **zero** back — its left
      rim pairs with `rightLimb.front` instead, because the arm's inner face
      sits 0.0026 studs off the torso's side and the nearest-point search picks
      it. A texel gets one partner, and at a corner the nearest neighbour is
      not always the fold you can see.

      Blending only the near end therefore halved the step and left the far side
      untouched — the seam moved rather than closed. Writing both ends from the
      same midpoint closes it with the links that do exist, and does not wait on
      the map learning to prefer a continuation within the same part.

      Both reads come from `before`, so a texel that is the far end of several
      links is computed from the original either way.
    */
    const wOwn = 0.5 * (1 - d / reach);
    const dOther = depths[to] as number;
    const wOther = dOther >= 0 && dOther < reach ? 0.5 * (1 - dOther / reach) : 0;
    if (wOwn <= 0 && wOther <= 0) continue;
    for (let c = 0; c < 3; c++) {
      const own = before[i * 4 + c] as number;
      const other = before[to * 4 + c] as number;
      if (wOwn > 0) data[i * 4 + c] = Math.round(own + (other - own) * wOwn);
      if (wOther > 0) data[to * 4 + c] = Math.round(other + (own - other) * wOther);
    }
    moved++;
  }
  return moved;
}

/** Whether a texel is within `depth` of the rim of whichever region holds it. */
function withinDepth(x: number, y: number, depth: number): boolean {
  for (const id of REGION_IDS) {
    const r = REGIONS[id];
    if (x < r.x || y < r.y || x >= r.x + r.w || y >= r.y + r.h) continue;
    return x - r.x < depth || r.x + r.w - x <= depth || y - r.y < depth || r.y + r.h - y <= depth;
  }
  return false;
}

/** Stage 5 — clear everything outside regions+gutters. */
export function floodDeadSpace(b: Bitmap, gutter = GUTTER_PX): Bitmap {
  /*
    Cleared, not blackened.

    This is the last stage, and it used to zero RGB along with alpha — undoing
    `bleedColour`'s work in exactly the places that need it most. Measured on a
    garment with a cut edge: twelve transparent texels adjacent to artwork came
    out pure black, all of them in the dead-space notches where `torso.down`
    meets the side faces.

    They are outside every face and gutter, so nothing samples them directly.
    A mip level does: bilinear at a face's edge reaches half a texel, level 1
    reaches one, and by level 2 it is past the 2px gutter and into this. Black
    there is a dark fringe at exactly the corners a sleeve or hem turns.

    Keeping the bled colour costs nothing — the texels stay fully transparent,
    so they are invisible in the file and correct under a filter.
  */
  return clipToRegions(b, REGION_IDS, gutter, { keepColour: true });
}

/** Run the full export pipeline. Mutates and returns `b`. */
/**
 * Run the full export pipeline. Mutates and returns `b`.
 *
 * The seam map is optional and body-specific, which is the one asymmetry in
 * here worth stating: every other stage is a property of the template alone, so
 * the same design exports identically whatever is on screen. Stage 2 is not —
 * which faces meet is a fact about a BODY, and two rigs disagree slightly. Given
 * no map, the stage is skipped rather than approximated, and the export is
 * exactly what it was before this existed.
 */
export function runEdgePipeline(b: Bitmap, settings: EdgeSettings, seam?: SeamMap | null): Bitmap {
  hardenAlpha(b, settings.alphaHardening);
  // Before dilation, deliberately. Dilation reads from whatever carries alpha
  // and pushes it into the gutter, so bleeding first means the gutter inherits
  // the joined-up edge instead of the cut one.
  if (settings.seamBleed && seam) seamBleed(b, seam, SEAM_BLEED_BAND);
  /*
    After the hole-fill, before the gutter is dilated.

    Order matters both ways: blending first would average against holes that
    stage 2 is about to fill properly, and dilating first would push the
    unblended edge colour into the gutter and then blend only what is inside
    the face, leaving the gutter carrying the old step.
  */
  if (settings.seamBlend && seam) seamBlend(b, seam, SEAM_BLEND_BAND);
  bleedColour(b, settings.colourBleedPasses);
  dilate(b, settings.dilationPasses);
  floodDeadSpace(b);
  return b;
}

/**
 * Whether stage 2 can run. Surfaced in the UI so the claim stays honest.
 *
 * Takes the map rather than answering in the abstract. The stage is built now,
 * but it needs a body's geometry to do anything, and "seam bleed is available"
 * is false in a way the user can act on when no body is loaded — the answer is
 * "open the preview", not "this app cannot do that".
 */
export function seamBleedAvailable(seam?: SeamMap | null): boolean {
  return Boolean(seam && seam.coverage > 0);
}
